import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RelatedRecipientService } from '@app/modules/communications/services/related-recipient.service';
import { DatasetRelatedAssetSelectedRecipient, DocumentSelectionInfo, MassNotification, MassRecipientType, RelatedAssetRecipientSettings } from '@app/modules/communications/store/communications';
import { Subject, forkJoin } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { Dataset, Document } from 'src/app/modules/core/store/models';
import { map, takeUntil } from 'rxjs/operators';
import { RelatedRecipientSelectionInfo, RelatedAssetsRecipientsSelectionContext } from '../related-assets-recipients-selection-list/related-assets-recipients-selection-list.component';
import { TemplateDesignerService } from '@app/modules/communications/services/template-designer.service';

@Component({
    selector: 'topo-comms-related-assets-recipients-selection',
    templateUrl: './related-assets-recipients-selection.component.html',
    styleUrls: ['./related-assets-recipients-selection.component.scss'],
    standalone: false
})
export class RelatedAssetsRecipientsSelectionComponent {
  @Input() notification: MassNotification;
  @Input() dataset: Dataset;
  @Input() document: Document;

  @Output() selectedRecipientsChanged = new EventEmitter<RelatedAssetRecipientSettings>();
  @Output() recipientCountChanged = new EventEmitter<number>();

  destroy$: Subject<void> = new Subject<void>();

  selectedRelatedRecipients?: DatasetRelatedAssetSelectedRecipient[];
  hasRelatedRecipients: boolean = false;
  relatedRecipientCount: number = 0;
  selectedRecipientTypeUId: string;
  recipientsSelectionActive: boolean;
  listSelectActiveContext: RelatedAssetsRecipientsSelectionContext;

  constructor(
    private relatedRecipientService: RelatedRecipientService,
    private templateDesignerService: TemplateDesignerService
  ) {}

  ngOnInit(): void {
    this.relatedRecipientService.init();

    this.selectedRelatedRecipients = cloneDeep(this.notification.recipientSettings?.relatedAssetSettings?.relatedAssetRecipients) ?? [];

    // get all related field datasets
    const allRelatedAssetRecipients = this.relatedRecipientService.getRelatedAssetsInfo(this.dataset?.uId);
    allRelatedAssetRecipients?.forEach(recipient => {
      if (!this.selectedRelatedRecipients.find(selectedRecipient => selectedRecipient.refFieldUId === recipient.refFieldUId))
      {
        this.selectedRelatedRecipients.push(recipient);
      }
    });

    forkJoin(this.selectedRelatedRecipients.map(selectedRecipient =>
      this.relatedRecipientService.getDatasetDocumentsRelatedCount(this.dataset.uId, this.document.uid, selectedRecipient)
      .pipe(takeUntil(this.destroy$))
      .pipe(map(count => ({ selectedRecipient, count })))
      )).subscribe(counts => {
        counts.forEach(count => {
          count.selectedRecipient.documentCount = count.count;
          count.selectedRecipient.include = false;
        });
        this.hasRelatedRecipients = this.selectedRelatedRecipients.length > 0;
        this.calculateTotalRecipients();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  assetInIndeterminateState(rel: DatasetRelatedAssetSelectedRecipient): boolean {
    return rel.include && this.getRelatedSelectedCount(rel) != rel.documentCount.total;
  }

  getRelatedSelectedCount(rel: DatasetRelatedAssetSelectedRecipient): number {
    if (rel.hasDocumentFilter === true) {
      return rel.documentCount.total;
    }
    const selected = rel.selectionInfo?.documentUIds?.length ?? 0;
    return rel.selectionInfo?.selectAll === true ? rel.documentCount.total - selected : selected;
  }

  isPartialSelected(rel: DatasetRelatedAssetSelectedRecipient): boolean {
    if (rel.include === false) {
      return false;
    }
    return rel.include && (this.getRelatedSelectedCount(rel) < rel.documentCount.total);
  }

  onRelatedRecipientSelectedChanged(
    relatedRecipient: DatasetRelatedAssetSelectedRecipient,
    event: any): void
  {
    event?.stopPropagation();

    relatedRecipient.include = !relatedRecipient.include;

    relatedRecipient.selectionInfo = {
      selectAll: relatedRecipient.include,
      documentUIds: []
    };

    this.notifyRecipientsChanged(this.selectedRelatedRecipients);
    this.calculateTotalRecipients();
  }

  onManageRecipient(uId: string): void {
    const relatedRecipient = this.selectedRelatedRecipients?.find(r => r.uId === uId);

    if (relatedRecipient.hasDocumentFilter) return;

    const newContext = {
      uId: uId,
      datasetUId: relatedRecipient.datasetUId,
      targetFilterGroup: null
    } as RelatedAssetsRecipientsSelectionContext;

    this.listSelectActiveContext = newContext;
  }

  getListSelectedUIds = (uId: string): DocumentSelectionInfo => {
    const selectedDataset = this.selectedRelatedRecipients?.find(d => d.uId === uId);
    return selectedDataset.selectionInfo ?? {} as DocumentSelectionInfo;
  };

  listSelectRecipientsSelected(selectionInfo: RelatedRecipientSelectionInfo) {
    const relatedRecipient = this.selectedRelatedRecipients.find(s => s.uId === selectionInfo.uId);
    if (!!!relatedRecipient.selectionInfo) {
      relatedRecipient.selectionInfo = { selectAll: false, documentUIds: [] };
    }

    relatedRecipient.selectionInfo.selectAll = selectionInfo.selectInfo.selectAll;
    relatedRecipient.selectionInfo.documentUIds = [];
    selectionInfo.selectInfo.documentUIds.forEach(id => relatedRecipient.selectionInfo.documentUIds.push(id));

    const selectedCount = this.getRelatedSelectedCount(relatedRecipient);
    relatedRecipient.include = selectedCount > 0;

    this.notifyRecipientsChanged(this.selectedRelatedRecipients);
    this.calculateTotalRecipients();
  }

  listSelectClosed() {
    this.listSelectActiveContext = null;
  }

  private calculateTotalRecipients(): void {
    this.relatedRecipientCount = 0;
    if (!!this.selectedRelatedRecipients) {
      this.selectedRelatedRecipients.forEach(r => {
        const selectedCount = r.include ? this.getRelatedSelectedCount(r) : 0;
        this.relatedRecipientCount += selectedCount;
      });
    }

    this.notifyRecipientCountChanged(this.relatedRecipientCount);
  }

  private notifyRecipientsChanged(recipients: DatasetRelatedAssetSelectedRecipient[]): void
  {
    this.selectedRecipientsChanged.emit({
      sendToRelatedAssets: this.selectedRelatedRecipients?.some(a => a.include),
      relatedAssetRecipients: recipients,
    });
  }

  private notifyRecipientCountChanged(count: number): void
  {
    this.recipientCountChanged.emit(count);
  }

  get includeRelatedRecipients() {
    return !this.partialIncludeRelatedRecipients && this.selectedRelatedRecipients?.some(a => a.include);
  }

  get partialIncludeRelatedRecipients() {
    return (
      this.selectedRelatedRecipients?.some(a => this.assetInIndeterminateState(a)) ||
      (this.selectedRelatedRecipients?.some(a => a.include == false) && this.selectedRelatedRecipients?.some(a => a.include == true))
    );
  }

  onIncludeRelatedRecipientsChanged() {
    const newIncludeAllState = !this.partialIncludeRelatedRecipients && !this.includeRelatedRecipients;

    this.selectedRelatedRecipients.forEach(a => {
      a.include = newIncludeAllState;
      a.selectionInfo = {
        selectAll: a.include,
        documentUIds: []
      };
    });

    this.notifyRecipientsChanged(this.selectedRelatedRecipients);
    this.calculateTotalRecipients();
  }
}
